import React, { useEffect, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  useWatch,
  Control,
  Controller,
} from "react-hook-form";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import { MASTER_URL } from "../../../common/common";
import AsynchronousDropdown from "../../../components/AutoComplete";
import Input from "../../../components/FormInputs";
import { ApiService } from "../../../service/api.service";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import { Plus, cancelButton } from "../../../common/svg";
import SecureImage from "../../../components/BucketImage/Image";

interface ClientDetailsProps {
  errors: FieldErrors;
  register: UseFormRegister<any>;
  setValue: any;
  control: Control<any>;
  setvehicleDetailsData: React.Dispatch<React.SetStateAction<any[]>>;
  vehicleDetailsData: any;
}

// Deep clone utility to break all references and avoid frozen arrays/objects
function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

const VehicleDetails: React.FC<ClientDetailsProps> = ({
  errors,
  register,
  setValue,
  control,
  setvehicleDetailsData,
  vehicleDetailsData,
}) => {
  const apiService = new ApiService();
  const [forms, setForms] = useState<number[]>([1]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formIdToRemove, setFormIdToRemove] = useState<number | null>(null);
  const [VehicleType, setVehicleType] = useState<any>([]);
  const [VehicleMode, setVehicleMode] = useState<any>([]);

  // Helper function to get field error message
  const getFieldError = (index: number, fieldName: string) => {
    return (
      (errors.vehicleDetails as Record<number, any>)?.[index]?.[fieldName]
        ?.message || ""
    );
  };

  // Helper function to check if field has error
  const hasFieldError = (index: number, fieldName: string) => {
    return !!(errors.vehicleDetails as Record<number, any>)?.[index]?.[
      fieldName
    ];
  };

  // Updated to match the pattern in the code snippet
  const watchFieldsArray = useWatch({
    control,
    name: forms
      .map((_, index) => [
        `vehicleDetails[${index}].vehicleMode`,
        `vehicleDetails[${index}].vehicleType`,
        `vehicleDetails[${index}].vehicleCount`,
        `vehicleDetails[${index}].vehicleSpecification`,
        `vehicleDetails[${index}].remarks`,
      ])
      .flat(),
  }) as string[];

  const watchFields = Object.fromEntries(
    forms
      .map((_, index) => [
        `vehicleDetails[${index}].vehicleMode`,
        `vehicleDetails[${index}].vehicleType`,
        `vehicleDetails[${index}].vehicleCount`,
        `vehicleDetails[${index}].vehicleSpecification`,
        `vehicleDetails[${index}].remarks`,
      ])
      .flat()
      .map((key, i) => [key, watchFieldsArray[i] || ""])
  );

  useEffect(() => {
    const getMasterData = async () => {
      const response: any = await apiService.getApi(
        `${MASTER_URL}/booking/data-route?key=Booking Type`
      );
      if (response.status === "200") {
        if (response.body["Vehicle Type"]) {
          setVehicleType(
            response.body["Vehicle Type"].map((item: any) => ({
              value: item.metaId,
              label: item.value,
            }))
          );
        }
        if (response.body["Route"]) {
          setVehicleMode(
            response.body["Route"].map((item: any) => ({
              value: item.metaId,
              label: item.value,
            }))
          );
        }
      }
    };
    getMasterData();
  }, []);

  // Updated to use the new watchFields pattern
  const watchedVehicleDetails = useWatch({ control, name: "vehicleDetails" });

  useEffect(() => {
    if (!Array.isArray(watchedVehicleDetails)) return;

    setvehicleDetailsData(watchedVehicleDetails);
  }, [watchedVehicleDetails]);

  // }, [watchFields, forms, setValue, setvehicleDetailsData]);

  // Helper for robust, immutable updates
  const setVehicleDetailField = (index: number, field: string, value: any) => {
    const allValues = control._formValues || {};
    const details = Array.isArray(allValues.vehicleDetails)
      ? deepClone(allValues.vehicleDetails)
      : [];
    while (details.length <= index) details.push({});
    details[index] = { ...details[index], [field]: value };
    setValue("vehicleDetails", details);
  };

  const handleAddForm = () => {
    setForms((prevForms) => [...prevForms, prevForms.length + 1]);
    // Optionally, add a blank entry to vehicleDetails
    const details = Array.isArray(vehicleDetailsData)
      ? deepClone(vehicleDetailsData)
      : [];
    details.push({});
    setValue("vehicleDetails", details);
  };

  const openModal = (formId: number) => {
    setFormIdToRemove(formId);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleConfirmRemove = () => {
    if (formIdToRemove !== null) {
      setForms((prevForms) => prevForms.filter((id) => id !== formIdToRemove));
      // Deep clone and remove the entry from vehicleDetails
      const details = Array.isArray(vehicleDetailsData)
        ? deepClone(vehicleDetailsData)
        : [];
      details.splice(formIdToRemove, 1);
      setValue("vehicleDetails", details);
      setFormIdToRemove(null);
      closeModal();
    }
  };
  vehicleDetailsData && console.log(vehicleDetailsData);

  return (
    <div className="bg-white p-4 rounded-[20px]">
      <div className="flex items-center justify-between">
        <FormTitle title="Vehicle Details" />
        <CustomButton label="" onClick={handleAddForm} icon={Plus} />
      </div>
      <div className="overflow-x-auto grid grid-cols-1 gap-4 ml-1">
        {forms.map((formId, index) => (
          <div key={formId} className="gap-4 border-b border-dashed pb-2">
            <div className="flex items-center justify-end mb-2 gap-4">
              <p className="text-[14px] font-[600]">
                #{(index + 1).toString().padStart(2, "0")}
              </p>
              {index >= 1 && (
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => openModal(formId)}
                    type="button"
                    className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                    aria-label="Remove"
                  >
                    <SecureImage
                      fileKey={cancelButton}
                      alt="down arrow"
                      width="15px"
                    />
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4 ml-1">
              <Controller
                name={`vehicleDetails[${index}].vehicleMode`}
                control={control}
                defaultValue={vehicleDetailsData?.[index]?.vehicleMode ?? ""}
                render={({ field }) => (
                  <CustomDropDown
                    label="Vehicle Mode"
                    name={`vehicleDetails[${index}].vehicleMode`}
                    options={VehicleMode}
                    register={register}
                    errors={errors}
                    defaultValue={field.value}
                    onChange={(value) => setVehicleDetailField(index, "vehicleMode", value)}
                  />
                )}
              />

              <Controller
                name={`vehicleDetails[${index}].vehicleType`}
                control={control}
                defaultValue={vehicleDetailsData?.[index]?.vehicleType ?? ""}
                render={({ field }) => (
                  <CustomDropDown
                    label="Vehicle Type"
                    name={`vehicleDetails[${index}].vehicleType`}
                    options={VehicleType}
                    register={register}
                    errors={errors}
                    defaultValue={field.value}
                    onChange={(value) => setVehicleDetailField(index, "vehicleType", value)}
                  />
                )}
              />

              <Input
                label="No of Vehicles"
                name={`vehicleDetails[${index}].vehicleCount`}
                errors={
                  hasFieldError(index, "vehicleCount")
                    ? {
                        [`vehicleDetails[${index}].vehicleCount`]: {
                          message: getFieldError(index, "vehicleCount"),
                        },
                      }
                    : {}
                }
                register={register}
                placeholder="Enter No of Vehicles"
                type="number"
                maxLength={10}
                defaultValue={vehicleDetailsData?.[index]?.vehicleCount ?? ""}
                onChange={(value) => setVehicleDetailField(index, "vehicleCount", value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 ml-1 mt-4">
              <Input
                label="Vehicle Specifications"
                name={`vehicleDetails[${index}].vehicleSpecification`}
                errors={
                  hasFieldError(index, "vehicleSpecification")
                    ? {
                        [`vehicleDetails[${index}].vehicleSpecification`]: {
                          message: getFieldError(index, "vehicleSpecification"),
                        },
                      }
                    : {}
                }
                register={register}
                placeholder="Enter Cargo Specifications"
                maxLength={500}
                multiline
                rows={2}
                defaultValue={vehicleDetailsData[index]?.vehicleSpecification}
                onChange={(value) => setVehicleDetailField(index, "vehicleSpecification", value)}
              />
              <Input
                label="Remarks"
                name={`vehicleDetails[${index}].remarks`}
                errors={
                  hasFieldError(index, "remarks")
                    ? {
                        [`vehicleDetails[${index}].remarks`]: {
                          message: getFieldError(index, "remarks"),
                        },
                      }
                    : {}
                }
                register={register}
                placeholder="Enter Remarks"
                maxLength={500}
                multiline
                rows={2}
                defaultValue={vehicleDetailsData[index]?.remarks}
                onChange={(value) => setVehicleDetailField(index, "remarks", value)}
              />
            </div>
          </div>
        ))}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center">
            <p className="text-[18px] font-[600] text-[#4B4B4B]">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default VehicleDetails;
